import { useRef, useEffect } from 'react';
import { type NonOptional } from '@Types';
import { type AudioVisualizerConfig } from '../TutorBotVoiceAgent.types';
import { createHexagons } from './createHexagons';
import { type Hexagon } from './Hexagon';
import { setupCanvasAndReturnContextAndSizeInfo } from './setupCanvasAndReturnContextAndSizeInfo';
import { useAnimateHexagons } from './useAnimateHexagons';
import { type useTrackAudioData } from './useTrackAudioData';

type Props = AudioVisualizerConfig & {
    size: 'xs' | undefined;
    getUserAudioData: ReturnType<typeof useTrackAudioData>['getUserAudioData'];
    getAgentAudioData: ReturnType<typeof useTrackAudioData>['getAgentAudioData'];
};

const initializeHexagons = (
    sizeInfo: NonNullable<Awaited<ReturnType<typeof setupCanvasAndReturnContextAndSizeInfo>>>,
    hexagonsRef: React.MutableRefObject<Hexagon[]>,
    hexagonsBackRef: React.MutableRefObject<Hexagon[]>,
    size: 'xs' | undefined,
) => {
    if (hexagonsRef.current.length === 0) {
        const { backGroundHexagons, foregroundHexagons } = createHexagons({
            ...sizeInfo,
            visualizerSize: size,
        });
        hexagonsRef.current = foregroundHexagons;
        hexagonsBackRef.current = backGroundHexagons;
    }
};

const setupVisualizer = (
    canvas: HTMLCanvasElement | null,
    gradientColors: { inner: string; outer: string },
    hexagonsRef: React.MutableRefObject<Hexagon[]>,
    hexagonsBackRef: React.MutableRefObject<Hexagon[]>,
    animateHexagons: ReturnType<typeof useAnimateHexagons>,
    size: 'xs' | undefined,
) => {
    const sizeInfo = setupCanvasAndReturnContextAndSizeInfo(canvas, size);
    if (!sizeInfo) return () => {};

    initializeHexagons(sizeInfo, hexagonsRef, hexagonsBackRef, size);

    return animateHexagons({
        ...sizeInfo,
        hexagonsRef,
        hexagonsBackRef,
        gradientColors,
    });
};

// oxlint-disable-next-line max-lines-per-function
export const useAudioVisualizer = ({
    size,
    gradientColors,
    getUserAudioData,
    getAgentAudioData,
}: NonOptional<Props>) => {
    const hexagonsRef = useRef<Hexagon[]>([]);
    const hexagonsBackRef = useRef<Hexagon[]>([]);
    const animateHexagons = useAnimateHexagons({ getUserAudioData, getAgentAudioData });
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        let frame: number;
        let cleanup: () => void;
        const setupWhenReady = () => {
            if (canvasRef.current?.getBoundingClientRect()?.width) {
                cleanup = setupVisualizer(
                    canvasRef.current,
                    gradientColors,
                    hexagonsRef,
                    hexagonsBackRef,
                    animateHexagons,
                    size,
                );
            } else {
                frame = requestAnimationFrame(setupWhenReady);
            }
        };

        setupWhenReady();

        return () => {
            cleanup?.();

            cancelAnimationFrame(frame);
        };
    }, [animateHexagons, canvasRef, gradientColors, size]);

    return canvasRef;
};
